import React, { useEffect, useMemo, useState, useRef } from "react";
import "./largeAccount.css";
import { countryOperatorChangeStatus, DeleteDND, fetchCountryOperatorList, findDNDList, GetCountries, getCountryCodeList } from "../../../api/Api";
import Swal from "sweetalert2";
import AddDndModal from "./addDndNumber";
import "./settingConf.css";
import { getFromLocalStorage } from "../../../utils/common-utils";
import CustomTable from "../../../components/Table/CustomTable";
import ViewDND from "./viewDND";
import { Navigate } from "react-router-dom";
import PaginationComponent from "../../../components/Pagination/PaginationComponent";
import AddCountryOperatorModal from "./addCountryOperatorModal";
import { useNavigate, useLocation } from "react-router-dom";
import "./manageCountryOperator.css";
import { GetContentProviderDataNew } from "../../../api/Api";

const ManageCountryOperator = () => {
  const [loaders, setLoaders] = useState({
    countryLoader: false,
    cpLoader: false,
  });
  const [countryList, setCountryList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [dndList, setDndList] = useState([]);
  const [addCountryOperatorModal, setAddCountryOperatorModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [viewDndModal, setViewDndModal] = useState(false);
  const [tableConfig, setTableConfig] = useState({
    enabledColumns: [],
    actions: [],
  });
  const dropdownRef = useRef(null);
  const [paginationData, setPaginationData] = useState({
    totalElements: 0,
    totalPages: 0
  });
  const [countryOperatorData, setCountryOperatorData] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [highlightedId, setHighlightedId] = useState(null);
  const [cpList, setCpList] = useState([]);
  const [contentPartner, setContentPartner] = useState("");
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [serviceListData, setServiceListData] = useState([]);
  const [servicePackageListData, setServicePackageListData] = useState([]);
  const [messageDescription, setMessageDescription] = useState("");

  // Fetch data function
  const fetchData = async () => {
    setLoaders(prev => ({ ...prev, countryLoader: true }));
    try {
      const payload = {
        pageNo: currentPage,
        pageSize: pageSize,
        userId: getFromLocalStorage("userData")?.id
      };
      const response = await fetchCountryOperatorList(payload);

      if (response?.statusDescription?.status === 200) {
        // Map the data to include actions for each row
        const dataWithActions = response.countryResponse.content.map(item => ({
          ...item,
          actions: response.action // Add actions to each row
        }));

        setCountryOperatorData(dataWithActions);
        setTableConfig({
          enabledColumns: response.enabledColumns,
          actions: response.action
        });
        setPaginationData({
          totalElements: response.countryResponse.page.totalElements,
          totalPages: response.countryResponse.page.totalPages
        });
      } else {
        // Swal.fire({
        //   icon: "error",
        //   title: "Error",
        //   text: response?.statusDescription?.description || "Failed to fetch data",
        // });
        setMessageDescription(response?.statusDescription?.description || "Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching country operator list:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch country operator data",
      });
    } finally {
      setLoaders(prev => ({ ...prev, countryLoader: false }));
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  // Reset operator when country changes
  useEffect(() => {
    setSelectedOperator(null);
  }, [selectedCountry]);

  useEffect(() => {
    const selectedId = localStorage.getItem('selectedCountryOperatorId');
    if (selectedId) {
      setHighlightedId(Number(selectedId));
      localStorage.removeItem('selectedCountryOperatorId');
    }
  }, []);

  // Update the useEffect for getCpList
  useEffect(() => {
    // Only call getCpList if both country and operator are selected
    if ((selectedCountry && selectedOperator) ||
      (selectedCountry?.countryName === "All")) {
      getCpList();
    }
  }, [selectedCountry, selectedOperator]);

  // Update getCpList function
  const getCpList = async () => {
    // Don't proceed if required selections aren't made
    if (!selectedCountry) return;

    setLoaders((prev) => ({ ...prev, cpLoader: true }));
    try {
      let payload = {
        pageNo: 0,
        pageSize: 1000,
        userId: getFromLocalStorage("userData")?.id,
        status: 1,
        countryId: selectedCountry?.countryName === "All" ? "All" : selectedCountry?.cnUniId,
        operatorId: selectedCountry?.countryName === "All" ? "All" :
          selectedOperator?.operatorName === "All" ? "All" :
            selectedOperator?.opUniId
      };

      const response = await GetContentProviderDataNew(payload);

      if (response?.statusDescription?.status === 200) {
        setCpList(response?.userListPage?.content);
      } else if (response?.statusDescription?.status === 401) {
        handleUnauthorized();
      } else {
        setCpList([]);
        if (response?.statusDescription?.status !== 210) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response?.statusDescription?.description || "Failed to fetch content providers"
          });
        }
      }
    } catch (error) {
      console.error("Error fetching content providers:", error);
      setCpList([]);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch content providers"
      });
    } finally {
      setLoaders((prev) => ({ ...prev, cpLoader: false }));
    }
  };

  // Update handleCountryChange function
  const handleCountryChange = (e) => {
    const value = e.target.value;
    if (value === "All") {
      setSelectedCountry({ countryName: "All", cnUniId: "All" });
      setSelectedOperator({ operatorName: "All", opUniId: "All" });
      setContentPartner("All");
      setSelectedService({ id: "All" });
      setSelectedPackage({ id: "All" });
      // Clear the lists since they're not needed when "All" is selected
      setServiceListData([]);
      setServicePackageListData([]);
      // Trigger CP list fetch with "All"
      getCpList();
    } else {
      const country = countryList.find((c) => c.countryName === value);
      setSelectedCountry(country);
      // Reset other fields
      setSelectedOperator(null);
      setContentPartner("");
      setSelectedService(null);
      setSelectedPackage(null);
      setCpList([]); // Clear CP list until operator is selected
    }
  };

  // Update handleOperatorChange function
  const handleOperatorChange = (e) => {
    const value = e.target.value;
    if (value === "All") {
      setSelectedOperator({ operatorName: "All", opUniId: "All" });
      setContentPartner("All");
      setSelectedService({ id: "All" });
      setSelectedPackage({ id: "All" });
      // Trigger CP list fetch with "All" operator
      getCpList();
    } else {
      const operator = selectedCountry?.operatorList.find(
        (op) => op.operatorName === value
      );
      setSelectedOperator(operator);
      setContentPartner("");
      setSelectedService(null);
      setSelectedPackage(null);
      // Trigger CP list fetch with specific operator
      getCpList();
    }
  };

  // Handle table actions
  const handleTableAction = async (action, rowData) => {
    if (action.action === "Change Status") {
      const isActive = rowData.status == 1;

      Swal.fire({
        title: "Are you sure?",
        text: isActive
          ? "Do you want to deactivate this country?"
          : "Do you want to activate this country?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          try {
            const payload = {
              userId: getFromLocalStorage("userData")?.id,
              status: isActive ? 0 : 1,
              id: rowData.id
            };

            const response = await countryOperatorChangeStatus(payload);
            if (response?.statusDescription?.status === 200) {
              return response;
            } else if (response?.statusDescription?.status === 401) {
              handleUnauthorized();
            } else {
              throw new Error(response?.statusDescription?.description || "Failed to update status");
            }
          } catch (error) {
            return Swal.showValidationMessage(
              error.message || "Something went wrong. Please try again."
            );
          }
        },
        allowOutsideClick: () => !Swal.isLoading()
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: result.value?.statusDescription?.description || `Country has been ${isActive ? 'deactivated' : 'activated'} successfully`
          });
          fetchData(); // Refresh the table data
          // If you have a count function like in the reference, add it here
          // getCount();
        }
      });
    } else if (action?.id === 149) {
      localStorage.setItem('selectedCountryOperatorId', rowData.id);
      navigate("/setting-&-configuration/add-operator", {
        state: rowData,
      });
    }
  };

  // Add handleUnauthorized function
  const handleUnauthorized = () => {
    Swal.fire("Info!", "Session expired, please login again", "info");
    localStorage.clear();
    navigate("/");
  };

  console.log("cccccc", dndList);
  return (
    <div className="content-wrapper">
      <section className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="box">
              <div className="box-header">
                <div className="row">
                  <div className="col-md-8 col-12">
                    <nav aria-label="breadcrumb">
                      <ol className="breadcrumb breadcrumb_bg">
                        <li className="breadcrumb-item">
                          <a className="breadcrumb_1">
                            Setting & Configuration
                          </a>
                        </li>
                        <li
                          className="breadcrumb-item active breadcrumb_2 d-flex align-items-center"
                          aria-current="page"
                        >
                          <span>Add Country Operator</span>
                        </li>
                      </ol>
                    </nav>
                  </div>
                  <div className="col-md-4 col-12 mt_3 d-flex align-items-center justify-content-end">
                    <div className="btn-group float-right">
                      <button
                        type="button"
                        className="btn btn-primary btn-raised btn-flat pull-right create_cp"
                        onClick={() => setAddCountryOperatorModal(true)}
                      >
                        Add Country
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="box-body px-md-4">
                {countryOperatorData.length > 0 ? (
                  <div className="table-container">
                    {/* Entries selector and refresh button */}
                    <div className="form-group d-flex align-items-center justify-content-between gap-2 records">
                      <div className="d-flex">
                        <span>Show</span>
                        <select
                          className="form-select count_select mx-2"
                          value={pageSize}
                          onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(0);
                          }}
                          style={{ width: "auto" }}
                        >
                          {[10, 25, 50, 100].map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                        <span>entries</span>
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-primary ref_but"
                          onClick={fetchData}
                        >
                          Refresh &nbsp;
                          <i className="fas fa-sync ms-auto"></i>
                        </button>
                      </div>
                    </div>

                    {/* Table */}
                    <div id="table">
                      <CustomTable
                        data={countryOperatorData}
                        enabledColumns={tableConfig.enabledColumns}
                        actions={tableConfig.actions}
                        onAction={handleTableAction}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPageChange={(page) => setCurrentPage(page)}
                        showPagination={true}
                        totalElements={paginationData.totalElements}
                        totalPages={paginationData.totalPages}
                        rowClassName={(row) => row.id === highlightedId ? 'highlighted-row' : ''}

                      />
                    </div>
                    {/* Pagination info */}
                    <div
                      className="d-flex justify-content-between align-items-center flex-column flex-md-row w-100"
                      id="pagination"
                    >
                      <div className="mr-3 line_pagination px-2 px-md-0">
                        Showing {currentPage * pageSize + 1} to{" "}
                        {Math.min(
                          (currentPage + 1) * pageSize,
                          paginationData.totalElements
                        )}{" "}
                        of {paginationData.totalElements} entries
                      </div>
                      <div className="pages">
                        <PaginationComponent
                          currentPage={currentPage}
                          onPageChange={(page) => setCurrentPage(page)}
                          totalPages={paginationData.totalPages}
                        />
                      </div>
                    </div>
                  </div>
                  ):(
                  <div className="text-center">
                    <h5>{messageDescription}</h5>
                  </div>
                ) }
              </div>
            </div>
          </div>
        </div>
      </section>

      <AddCountryOperatorModal
        show={addCountryOperatorModal}
        onHide={() => setAddCountryOperatorModal(false)}
        countryList={countryList}
        onSuccess={() => {
          fetchData();
          setAddCountryOperatorModal(false);
        }}
      />
    </div>
  );
};

export default ManageCountryOperator;
